import {
  Button,
  Card,
  Col,
  DatePicker,
  Radio,
  Row,
  Select,
  Input,
  Modal,
  Form,
  AutoComplete,
  Alert,
  Switch,
  Tooltip,
  Typography,
  Divider,
} from "antd";
import { get, isEmpty, map, keyBy } from "lodash";
import moment from "moment-timezone";
import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useApolloClient, useQuery } from "@apollo/client";
import { GET_ALL_REQUIREMENTS } from "../../../graphql/requirements";

import { DocumentPreview } from "../../../components/documentPreview";
import {
  DocumentStatus,
  MISCELLANEOUS_APPROVAL_WARNING_MESSAGE,
} from "../../../constants/documents";
import { checkIsMiscellaneousApprovalRestricted } from "../../../utils/documents";
import { FullScreenModal } from "./style";
import { ImageViewer } from "../../../components/imageViewer";
import { GET_DOCUMENT_LIST } from "./gql";
import { useDeleteWorkerDocument } from "@src/appV2/WorkerDocuments/api/useDeleteWorkerDocument";
import { useUpdateWorkerDocument } from "@src/appV2/WorkerDocuments/api/useUpdateWorkerDocument";
import { GET_HCP_PENDING_DOCS_COUNT } from "../../accountManagement/agents/gql";
import {
  EXPIRY_TYPE,
  DRIVING_LICENSE_STATE_ID_REQUIREMENT_ID,
} from "src/containers/requirements/constants";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { logEvent, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { isDefined } from "@clipboard-health/util-ts";
import { DocumentZendeskTickets } from "@src/appV2/WorkerDocuments/components/DocumentZendeskTickets";
import { useGetRejectionMappings } from "./useGetRejectionMappings";
import { useGetDocumentEvidences } from "./useGetDocumentEvidences";
import { useUpdateEvidence } from "@src/appV2/redesign/Evidence/api/useUpdateEvidence";
import { DocumentEvidenceSection } from "./DocumentEvidenceSection";
import { cleanProperties, getErrorMessage } from "@src/appV2/redesign/Evidence/utils";
import { toJsonSchema } from "@src/appV2/redesign/Evidence/types";
import { useGetAllEvidenceDefinitions } from "@src/appV2/redesign/Evidence/api/useGetAllEvidenceDefinitions";
import { useForm, type FieldValues } from "react-hook-form";

const { Item: FormItem } = Form;
const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

type EvidenceStatus = "IN_REVIEW" | "APPROVED" | "REJECTED";

const DOCUMENT_STATUS_TO_EVIDENCE_STATUS: Record<string, EvidenceStatus> = {
  [DocumentStatus.PENDING]: "IN_REVIEW",
  [DocumentStatus.APPROVED]: "APPROVED",
  [DocumentStatus.REJECTED]: "REJECTED",
};

const InfoVariableDescription = (
  <div>
    <ol>
      <li>{"{{documentName}}"} - will be replaced by the actual document name </li>
      <li>{"{{reason}}"} - will be replaced by the actual reason selected </li>
    </ol>
  </div>
);

function Component({ visible, document, onCancel, onUpdated, agentTMZ }) {
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [documentInfo, setDocumentInfo] = useState<any>({});
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedRequirement, setSelectedRequirement] = useState<any>({});
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [notification, setNotification] = useState<any>({});
  const [isUpdating, setUpdating] = useState(false);
  const [isDeleting, setDeleting] = useState(false);
  const [imagePath, setImagePath] = useState<string>();
  const [evidenceDefinitionId, setEvidenceDefinitionId] = useState("");
  const { data, loading } = useQuery(GET_ALL_REQUIREMENTS);
  const requirements = data && keyBy(data.allRequirements, "_id");
  const { mutateAsync: deleteWorkerDocument } = useDeleteWorkerDocument();
  const { mutateAsync: updateWorkerDocument } = useUpdateWorkerDocument();
  const apolloClient = useApolloClient();

  const { data: rejectionMappingsResponse, isLoading: isRejectionMappingsLoading } =
    useGetRejectionMappings({
      staleTime: 5 * 60 * 1000,
    });

  const docsV2ToolsFlag = useCbhFlag(CbhFeatureFlag.DOCS_V2_TOOLS_IN_ADMIN_UI, {
    defaultValue: { isEnabled: false },
  });
  const isDocsV2Enabled = docsV2ToolsFlag.isEnabled;

  const {
    evidences: documentEvidences,
    isLoading: isEvidenceLoading,
    isError: isEvidenceFetchError,
  } = useGetDocumentEvidences(isDocsV2Enabled ? (document?._id ?? "") : "");
  const { mutateAsync: updateEvidence } = useUpdateEvidence();
  const { definitions: allDefinitions, isError: isDefinitionsError } =
    useGetAllEvidenceDefinitions();

  const evidenceForDocument =
    isDocsV2Enabled && documentEvidences.length === 1 ? documentEvidences[0] : null;

  const selectedDefinition = allDefinitions.find((d) => d.id === evidenceDefinitionId);
  const isUnknownDefinitionSelected =
    selectedDefinition?.attributes.displayName.includes("UNKNOWN") ?? false;

  const evidenceForm = useForm<FieldValues>({
    mode: "onSubmit",
    defaultValues: { properties: {} },
  });

  // Initialize evidence form when evidence loads
  useEffect(() => {
    if (!evidenceForDocument) {
      return;
    }

    const properties = evidenceForDocument.attributes.properties as Record<string, unknown>;
    evidenceForm.reset({ properties });
    setEvidenceDefinitionId(evidenceForDocument.relationships.evidenceDefinition.data.id);
  }, [evidenceForDocument]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show error and close if multiple evidences found for document
  useEffect(() => {
    if (isDocsV2Enabled && documentEvidences.length > 1) {
      showErrorToast("Multiple evidences found for this document. Please contact support.");
      onCancel();
    }
  }, [isDocsV2Enabled, documentEvidences, onCancel]);

  useEffect(() => {
    if (loading) {
      return;
    }

    initDocumentInfo();

    // set default notification value
    setNotification({
      note: "",
      reason: "",
      pushNotification: document?.status !== DocumentStatus.REJECTED,
    });
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, loading]);

  useEffect(() => {
    setSelectedRequirement(requirements ? requirements[documentInfo.category] : {});
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentInfo.category]);

  useEffect(() => {
    if (!selectedRequirement?.visibleToHCP) {
      setNotification({ ...notification, pushNotification: false });
    }
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequirement]);

  const initDocumentInfo = () => {
    if (!document) {
      setDocumentInfo({});
      return;
    }
    setDocumentInfo({
      ...document,
      category: document.fulfilledRequirement?._id || null,
      expiryType: get(requirements, `${document.fulfilledRequirement?._id}.expiryType`, null),
      expiry: document.expiry && moment(document.expiry),
      issueDate: document.issueDate && moment(document.issueDate),
      displayName: document?.fulfilledRequirement?.name,
      subType: document?.subType || "",
    });
  };

  const onChange = (key) => (value) => setDocumentInfo((state) => ({ ...state, [key]: value }));

  const handleReasonSelection = (
    selectedReason: string,
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rejectionMappingsData: any[] | undefined
  ) => {
    if (!selectedReason || !rejectionMappingsData) {
      return;
    }

    const rejectionMapping = rejectionMappingsData.find(
      (mapping) => mapping.attributes.title === selectedReason
    );

    const pushNotificationTemplate = rejectionMapping?.attributes.pushNotificationTemplate;
    if (pushNotificationTemplate) {
      const autoFilledNote = pushNotificationTemplate
        .replace(/{{documentName}}/g, documentInfo.displayName || "document")
        .replace(/{{reason}}/g, selectedReason);

      if (isDefined(autoFilledNote)) {
        setNotification((prev) => ({ ...prev, note: autoFilledNote }));
      }
    }

    if (isDefined(rejectionMapping?.attributes.pushNotificationEnabled)) {
      setNotification((prev) => ({
        ...prev,
        pushNotification: rejectionMapping.attributes.pushNotificationEnabled,
      }));
    }
  };

  const onRejectionInfoChange =
    (key) =>
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    (data: string | boolean | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> = "") => {
      let value: string | boolean;
      if (typeof data === "boolean") {
        value = data;
      } else if (typeof data === "object" && data.target) {
        value = data.target.value;
      } else {
        value = data as string;
      }
      setNotification({ ...notification, [key]: value });
    };

  const onCategoryChange = (callback) => (value) => {
    callback(value);
    const displayName = get(requirements, `${value}.name`, "");
    const expiryType = get(requirements, `${value}.expiryType`, null);
    setDocumentInfo((state) => ({ ...state, displayName, expiryType }));
  };

  const handleEmptyDisplayName = () => {
    const name = documentInfo.uploadedFor?.name;
    if (documentInfo.displayName || !name) {
      return;
    }

    setDocumentInfo((state) => ({ ...state, displayName: name }));
  };

  // cspell:ignore Emptycategory
  const handleEmptycategory = () => {
    const _id = documentInfo.uploadedFor?._id;
    if (!isEmpty(documentInfo.category) || !_id) {
      return;
    }

    setDocumentInfo((state) => ({ ...state, category: String(_id) }));
  };

  const onExpiryChange = (callback) => (value) => {
    callback(value);
    handleEmptyDisplayName();
    handleEmptycategory();
  };

  const onInputChange =
    (key) =>
    ({ target }) => {
      const newValue = target.value;

      if (key === "status") {
        const currentStatus = documentInfo.status;
        const newStatus = newValue;

        const statusesWithNotification = [DocumentStatus.REJECTED, DocumentStatus.PENDING];
        const isCurrentStatusWithNotification = statusesWithNotification.includes(currentStatus);
        const isNewStatusWithNotification = statusesWithNotification.includes(newStatus);

        if (
          isCurrentStatusWithNotification &&
          isNewStatusWithNotification &&
          currentStatus !== newStatus
        ) {
          setNotification({
            reason: "",
            note: "",
            pushNotification: false,
          });
        } else if (isCurrentStatusWithNotification && !isNewStatusWithNotification) {
          setNotification({
            reason: "",
            note: "",
            pushNotification: false,
          });
        }
      }

      onChange(key)(newValue);
    };

  const showDisplayNameError = (uploadInfo) => {
    if (!uploadInfo.displayName) {
      showErrorToast("Please enter display name");
      return true;
    }
    return false;
  };

  const showExpiryError = (uploadInfo) => {
    const isNewDocumentApproved = uploadInfo.status === DocumentStatus.APPROVED;
    // cspell:ignore Fullfilled
    const isNewDocumentFullfilledRequirementPresent = isDefined(uploadInfo.fulfilledRequirementId);
    const isNewDocumentExpiryPresent = isDefined(uploadInfo.expiry) && !isEmpty(uploadInfo.expiry);
    const newDocumentHasExpiryMissing =
      isNewDocumentApproved &&
      isNewDocumentFullfilledRequirementPresent &&
      !isNewDocumentExpiryPresent;
    const isExpiryRemoved =
      isDefined(document.expiry) && !isEmpty(document.expiry) && !isNewDocumentExpiryPresent;
    const isNonExpiringRequirement =
      requirements[documentInfo.category]?.expiryType === EXPIRY_TYPE.DOES_NOT_EXPIRE;

    if (!isNonExpiringRequirement && (newDocumentHasExpiryMissing || isExpiryRemoved)) {
      showErrorToast("Please enter expiry");
      return true;
    }
    return false;
  };

  const showUploadValidationError = (uploadInfo) => {
    return showDisplayNameError(uploadInfo) || showExpiryError(uploadInfo);
  };

  const getUploadInfo = () => ({
    id: documentInfo._id,
    fulfilledRequirementId: documentInfo.category ?? undefined,
    displayName: documentInfo.displayName,
    status: documentInfo.status,
    expiry:
      !documentInfo.expiry ||
      requirements[documentInfo.category]?.expiryType === EXPIRY_TYPE.DOES_NOT_EXPIRE
        ? null
        : moment.tz(documentInfo.expiry, agentTMZ).endOf("day").toISOString(),
    issueDate: documentInfo.issueDate?.toISOString(),
    ...((documentInfo.status === DocumentStatus.REJECTED ||
      documentInfo.status === DocumentStatus.PENDING) && { notification }),
    subType: isPersonalId ? documentInfo?.subType : "",
  });

  const refetchPendingDocsQueries = async () => {
    try {
      await apolloClient.refetchQueries({
        include: [GET_DOCUMENT_LIST, GET_HCP_PENDING_DOCS_COUNT],
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      showErrorToast(error.message);
    }
  };

  const validateTemplateVariables = (noteText: string): string[] => {
    const templateVariableRegex = /\{\{([^}]+)\}\}/g;
    const matches: string[] = [];
    let match: RegExpExecArray | null;

    while ((match = templateVariableRegex.exec(noteText)) !== null) {
      matches.push(match[1]);
    }

    return matches;
  };

  const onUpdate = async () => {
    if (
      (documentInfo?.status === DocumentStatus.REJECTED ||
        documentInfo?.status === DocumentStatus.PENDING) &&
      notification.pushNotification &&
      (!notification.reason?.trim() || !notification.note?.trim())
    ) {
      const reasonType = documentInfo?.status === DocumentStatus.PENDING ? "pending" : "rejection";
      return showErrorToast(`You must enter a ${reasonType} reason and a note to HCP`);
    }

    if (
      (documentInfo?.status === DocumentStatus.REJECTED ||
        documentInfo?.status === DocumentStatus.PENDING) &&
      notification.pushNotification &&
      notification.note?.trim()
    ) {
      const unreplacedVariables = validateTemplateVariables(notification.note);
      if (unreplacedVariables.length > 0) {
        return Modal.error({
          title: "Template Variables Found",
          content: (
            <div>
              <p>Please replace the following template variables in your note before submitting:</p>
              <ul>
                {unreplacedVariables.map((variable, index) => (
                  // FIXME: Automatically disabled during ESLint config update
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={index}>
                    <code>{`{{${variable}}}`}</code>
                  </li>
                ))}
              </ul>
              <p>Replace these variables with actual values or remove them from your note.</p>
            </div>
          ),
        });
      }
    }

    const uploadInfo = getUploadInfo();
    if (showUploadValidationError(uploadInfo)) {
      return;
    }

    const isEvidenceApproved =
      evidenceForDocument &&
      evidenceDefinitionId &&
      documentInfo.status === DocumentStatus.APPROVED;

    if (isEvidenceApproved && isUnknownDefinitionSelected) {
      showErrorToast("Please select a known evidence definition before approving");
      return;
    }

    if (isEvidenceApproved) {
      const isEvidenceFormValid = await evidenceForm.trigger();
      if (!isEvidenceFormValid) {
        showErrorToast("Please fill in all required evidence fields");
        return;
      }
    }

    setUpdating(true);

    let documentUpdateFailed = false;
    let evidenceUpdateFailed = false;

    try {
      await updateWorkerDocument(uploadInfo);
      await refetchPendingDocsQueries();
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      documentUpdateFailed = true;
      showErrorToast(error.message);
    }

    if (documentUpdateFailed) {
      setUpdating(false);
      return;
    }

    if (isDefined(evidenceForDocument)) {
      const evidenceStatus = DOCUMENT_STATUS_TO_EVIDENCE_STATUS[documentInfo.status];

      const selectedDef = allDefinitions.find((d) => d.id === evidenceDefinitionId);
      const evidenceProperties = evidenceForm.getValues("properties");
      const cleanedProperties = selectedDef
        ? cleanProperties(evidenceProperties, toJsonSchema(selectedDef.attributes.propertySchema))
        : evidenceProperties;

      try {
        await updateEvidence({
          id: evidenceForDocument.id,
          params: {
            data: {
              type: "evidence",
              id: evidenceForDocument.id,
              attributes: {
                ...(evidenceStatus ? { status: evidenceStatus } : {}),
                ...(isEvidenceApproved ? { properties: cleanedProperties } : {}),
              },
              ...(isEvidenceApproved
                ? {
                    relationships: {
                      evidenceDefinition: {
                        data: {
                          type: "evidence-definition",
                          id: evidenceDefinitionId,
                        },
                      },
                    },
                  }
                : {}),
            },
          },
        });
      } catch (error: unknown) {
        evidenceUpdateFailed = true;
        showErrorToast(getErrorMessage(error));
      }
    }

    if (evidenceUpdateFailed) {
      logEvent(APP_V2_USER_EVENTS.EVIDENCE_UPDATE_FAILED, {
        documentId: documentInfo._id,
        evidenceId: evidenceForDocument?.id,
      });
      setUpdating(false);
      onUpdated();
      return;
    }

    setUpdating(false);
    onUpdated();
    onCancel();
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await deleteWorkerDocument({ id: documentInfo._id });
      await refetchPendingDocsQueries();
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      showErrorToast(error.message);
    }
    setDeleting(false);
    onCancel();
  };

  const handleImageViewerClose = () => {
    setImagePath("");
  };

  const confirmDelete = () => {
    Modal.confirm({
      title: `Are you sure you want to delete this document?`,
      onOk: onDelete,
    });
  };

  const filterRequirements = (input, { props }) =>
    props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  const isPersonalId = useMemo(() => {
    if (!documentInfo.category) {
      return false;
    }

    return String(documentInfo.category) === DRIVING_LICENSE_STATE_ID_REQUIREMENT_ID;
  }, [documentInfo]);

  const isMiscellaneousApprovalRestricted = useMemo(
    () =>
      checkIsMiscellaneousApprovalRestricted(
        requirements?.[documentInfo.category]?.name,
        documentInfo.status
      ),
    [documentInfo.category, documentInfo.status, requirements]
  );

  const personalVerificationDocumentOptions = useCbhFlag(CbhFeatureFlag.PERSONAL_ID_SUBTYPES, {
    defaultValue: {
      manual: [],
      stripe: [],
    },
  });

  return (
    <FullScreenModal visible={visible} title="Review Document" footer={null} onCancel={onCancel}>
      <Row style={{ height: "100%" }}>
        <Col span={12} style={{ height: "100%" }}>
          <ImageViewer
            visible={!!imagePath}
            images={[{ src: `${imagePath}`, alt: "Document" }]}
            onClose={handleImageViewerClose}
          />
          {/* @ts-expect-error FIXME this wrong type */}
          <DocumentPreview doc={documentInfo} setImagePath={setImagePath} isPreviewImage={true} />
        </Col>
        <Col span={12} style={{ height: "100%" }}>
          <Card style={{ width: "100%", height: "100%", overflow: "scroll" }} title="">
            <Form layout="vertical">
              <FormItem label="Category / Requirement">
                <Select
                  placeholder="Select Requirements"
                  optionFilterProp="children"
                  showSearch
                  onChange={onCategoryChange(onChange("category"))}
                  /* @ts-expect-error FIXME this wrong type */
                  filterOption={filterRequirements}
                  value={documentInfo.category}
                  loading={loading}
                >
                  {map(requirements, (requirement) => (
                    <Option key={requirement._id}>{requirement.name}</Option>
                  ))}
                </Select>
              </FormItem>
              {isPersonalId && (
                <FormItem label="ID type">
                  <Select
                    placeholder="choose type"
                    optionFilterProp="children"
                    labelInValue
                    data-testid="personal-id-subtype"
                    optionLabelProp="label"
                    value={documentInfo.subType || undefined}
                    onChange={(e) => {
                      setDocumentInfo((state) => ({
                        ...state,
                        subType: e.value,
                      }));
                    }}
                  >
                    {map(personalVerificationDocumentOptions.stripe, (option) => (
                      <Option
                        data-testid={`personal-id-option-${option}`}
                        key={option}
                        value={option}
                        label={option}
                      >
                        {option}
                      </Option>
                    ))}
                    {map(personalVerificationDocumentOptions.manual, (option, index) => {
                      const isFirstElement = index === 0;

                      return (
                        <Option
                          data-testid={`personal-id-option-${option}`}
                          key={option}
                          value={option}
                          label={option}
                        >
                          {option}
                          {isFirstElement && (
                            <Divider
                              style={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                margin: 0,
                              }}
                            ></Divider>
                          )}
                        </Option>
                      );
                    })}
                  </Select>
                </FormItem>
              )}
              {documentInfo.expiryType === EXPIRY_TYPE.DOES_NOT_EXPIRE && (
                <FormItem label="Expiry">
                  <Text>Does not expire</Text>
                </FormItem>
              )}

              {documentInfo.expiryType === EXPIRY_TYPE.ISSUE_PLUS_VALIDITY && (
                <FormItem label="Date of Document">
                  <DatePicker
                    value={documentInfo.issueDate}
                    format="MM/DD/YYYY"
                    onChange={onExpiryChange(onChange("issueDate"))}
                  />
                </FormItem>
              )}

              {(!documentInfo.expiryType ||
                documentInfo.expiryType === EXPIRY_TYPE.EXPIRY_DATE) && (
                <FormItem label="Expiry">
                  <DatePicker
                    value={documentInfo.expiry}
                    format="MM/DD/YYYY"
                    onChange={onExpiryChange(onChange("expiry"))}
                  />
                </FormItem>
              )}

              <FormItem label="Status">
                <Radio.Group
                  value={documentInfo.status}
                  buttonStyle="solid"
                  onChange={onInputChange("status")}
                >
                  <Radio.Button value={DocumentStatus.PENDING}>Needs Review</Radio.Button>
                  <Radio.Button value={DocumentStatus.APPROVED}>Approved</Radio.Button>
                  <Radio.Button value={DocumentStatus.REJECTED}>Rejected</Radio.Button>
                </Radio.Group>
                {isMiscellaneousApprovalRestricted && (
                  <div style={{ marginTop: "8px" }}>
                    <Text type="danger">{MISCELLANEOUS_APPROVAL_WARNING_MESSAGE}</Text>
                  </div>
                )}
              </FormItem>

              {documentInfo?.status === DocumentStatus.REJECTED && (
                <Card className={"notification-container"}>
                  <FormItem label="Reason for Rejection">
                    <AutoComplete
                      style={{ width: 200 }}
                      options={
                        rejectionMappingsResponse?.data.map((mapping) => ({
                          value: mapping.attributes.title,
                        })) ?? []
                      }
                      value={notification.reason}
                      placeholder={isRejectionMappingsLoading ? "Loading..." : "Reason"}
                      filterOption={(inputValue, option) =>
                        String(option?.value).toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                      }
                      allowClear={true}
                      onChange={(selectedReason) => {
                        setNotification({ ...notification, reason: selectedReason });
                        handleReasonSelection(
                          selectedReason,
                          rejectionMappingsResponse?.data || []
                        );
                      }}
                    />
                  </FormItem>
                  <FormItem label="Note to HCP">
                    <TextArea
                      rows={4}
                      disabled={!notification.pushNotification}
                      value={notification.note}
                      onChange={onRejectionInfoChange("note")}
                    />
                  </FormItem>

                  <FormItem name="a">
                    <div className={"note-container"}>
                      <p>Notify HCP</p>
                      <Tooltip
                        title={
                          !selectedRequirement?.visibleToHCP
                            ? "The document is not visible to HCP"
                            : ""
                        }
                        trigger="hover"
                      >
                        <Switch
                          onChange={onRejectionInfoChange("pushNotification")}
                          checked={notification.pushNotification}
                          disabled={!selectedRequirement?.visibleToHCP}
                        />
                      </Tooltip>
                    </div>
                  </FormItem>

                  {notification.pushNotification && (
                    <Alert
                      message="Available Variables"
                      description={InfoVariableDescription}
                      type="info"
                    />
                  )}
                </Card>
              )}

              {documentInfo?.status === DocumentStatus.PENDING && (
                <Card className={"notification-container"}>
                  <FormItem label="Reason for Pending Review">
                    <AutoComplete
                      style={{ width: 200 }}
                      options={
                        rejectionMappingsResponse?.data.map((mapping) => ({
                          value: mapping.attributes.title,
                        })) ?? []
                      }
                      value={notification.reason}
                      placeholder={isRejectionMappingsLoading ? "Loading..." : "Reason"}
                      filterOption={(inputValue, option) =>
                        String(option?.value).toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                      }
                      allowClear={true}
                      onChange={(selectedReason) => {
                        setNotification({ ...notification, reason: selectedReason });
                        handleReasonSelection(
                          selectedReason,
                          rejectionMappingsResponse?.data || []
                        );
                      }}
                    />
                  </FormItem>
                  <FormItem label="Note to HCP">
                    <TextArea
                      rows={4}
                      disabled={!notification.pushNotification}
                      value={notification.note}
                      onChange={onRejectionInfoChange("note")}
                    />
                  </FormItem>

                  <FormItem name="a">
                    <div className={"note-container"}>
                      <p>Notify HCP</p>
                      <Tooltip
                        title={
                          !selectedRequirement?.visibleToHCP
                            ? "The document is not visible to HCP"
                            : ""
                        }
                        trigger="hover"
                      >
                        <Switch
                          onChange={onRejectionInfoChange("pushNotification")}
                          checked={notification.pushNotification}
                          disabled={!selectedRequirement?.visibleToHCP}
                        />
                      </Tooltip>
                    </div>
                  </FormItem>

                  {notification.pushNotification && (
                    <Alert
                      message="Available Variables"
                      description={InfoVariableDescription}
                      type="info"
                    />
                  )}
                </Card>
              )}
              <DocumentZendeskTickets documentId={documentInfo._id} />
              {isDocsV2Enabled && isEvidenceFetchError && <Divider />}
              {isDocsV2Enabled && isEvidenceFetchError && (
                <Alert
                  type="warning"
                  message="Failed to load evidence data. Evidence will not be updated."
                  showIcon
                />
              )}
              {evidenceForDocument && documentInfo.status === DocumentStatus.APPROVED && (
                <DocumentEvidenceSection
                  evidence={evidenceForDocument}
                  selectedDefinitionId={evidenceDefinitionId}
                  onDefinitionChange={(definitionId) => {
                    setEvidenceDefinitionId(definitionId);
                    evidenceForm.reset({ properties: {} });
                  }}
                  control={evidenceForm.control}
                  errors={evidenceForm.formState.errors}
                />
              )}
              <FormItem className="align-center">
                <Button
                  type="primary"
                  size="large"
                  onClick={onUpdate}
                  loading={isUpdating}
                  disabled={
                    isMiscellaneousApprovalRestricted ||
                    (isDocsV2Enabled && isEvidenceLoading) ||
                    (isDocsV2Enabled && isEvidenceFetchError) ||
                    (isDocsV2Enabled && isDefinitionsError) ||
                    (isDocsV2Enabled &&
                      Boolean(evidenceForDocument) &&
                      documentInfo.status === DocumentStatus.APPROVED &&
                      (!evidenceDefinitionId || isUnknownDefinitionSelected || !selectedDefinition))
                  }
                >
                  Update
                </Button>
                <Button
                  /* @ts-expect-error FIXME this wrong type */
                  type="danger"
                  style={{ marginLeft: "8px" }}
                  size="large"
                  onClick={confirmDelete}
                  loading={isDeleting}
                >
                  Delete
                </Button>
              </FormItem>
            </Form>
          </Card>
        </Col>
      </Row>
    </FullScreenModal>
  );
}

function ReviewModal(props) {
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isAdmin = useSelector((state: any) => state.session?.admin);

  return isAdmin ? <Component {...props} /> : null;
}

export { ReviewModal };
